// App.jsx
import React, { useEffect, useState } from "react";
import { Row, Col, App, Space, Button, Spin } from "antd";
import "./App.css";
import { LoadingOutlined } from "@ant-design/icons";

// ✅ Correct paths after restructure
import LoginForm from "./components/checkout/LoginForm";
import Layout from "./Layout";
import { useAuth } from "./context/AuthContext";
import { cartAPI } from "./api/services";

const HEADER_HEIGHT = 64;

const MainApp = () => {
  const { notification } = App.useApp();
  const { isAuth } = useAuth();

  const params = new URLSearchParams(window.location.search);
  const cart = params.get("cart");
  const origin = params.get("origin");

  const [cartData, setCartData] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [appLoader, setAppLoader] = useState(true);
  const [headerTitle, setHeaderTitle] = useState(null);

  // ─── Fetch Cart ────────────────────────────────────────────────────────────
  const fetchCart = async () => {
    try {
      const res = await cartAPI.fetch(cart);
      if (res?.data) setCartData(res.data);
    } catch (error) {
      notification.error({ message: error.message });
    }
  };

  // ─── On mount ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuth) setAppLoader(false);
  }, []);

  // ─── Auto-redirect after success ─────────────────────────────────────────
  useEffect(() => {
    if (paymentStatus?.status === "success") {
      const timer = setTimeout(() => {
        try {
          window.parent.location.reload();
        } catch {
          window.parent.postMessage({ type: "CHECKOUT_CLOSE" }, "*");
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  // ─── Payment status screens ───────────────────────────────────────────────
  if (paymentStatus) {
    return <PaymentScreen status={paymentStatus.status} onRetry={() => setPaymentStatus(null)} />;
  }

  // ─── Missing params guard ─────────────────────────────────────────────────
  if (!cart || !origin) {
    return (
      <Col style={{ padding: 50 }}>
        {!cart && <div>Missing param 'cart'</div>}
        {!origin && <div>Missing param 'origin'</div>}
      </Col>
    );
  }

  // ─── Main checkout layout ─────────────────────────────────────────────────
  return (
    <>
      <Col style={{ height: "100vh" }}>
        {/* Header */}
        <Row
          align="middle"
          justify="space-between"
          style={{
            height: HEADER_HEIGHT,
            padding: "0 12px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          {headerTitle ? (
            <div style={{ fontSize: 18, fontWeight: 600, color: "#000" }}>
              {headerTitle}
            </div>
          ) : (
            <>
              <Col>
                <img src="/assets/logo.png" style={{ height: 32 }} alt="logo" />
              </Col>
              {cartData?.total_amount && (
                <Col style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#888" }}>Order Total</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {cartData.total_amount.currency} {parseFloat(cartData.total_amount.amount).toFixed(2)}
                  </div>
                </Col>
              )}
              {/* X is handled by parent Shopify modal — clicking it dispatches exit event */}
            </>
          )}
        </Row>

        {/* Body */}
        <Row
          justify="center"
          style={{
            height: `calc(100% - ${HEADER_HEIGHT}px)`,
            overflowY: "auto",
          }}
        >
          <Col flex={1} className="app-container-x">
            {isAuth ? (
              <Layout
                fetchCart={fetchCart}
                cartData={cartData}
                setCartData={setCartData}
                setPaymentStatus={setPaymentStatus}
                setAppLoader={setAppLoader}
                setHeaderTitle={setHeaderTitle}
              />
            ) : (
              <LoginForm origin={origin} />
            )}
          </Col>
        </Row>
      </Col>

      {/* App loader overlay */}
      {appLoader && <AppLoader />}
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const PaymentScreen = ({ status, onRetry }) => {
  const screens = {
    pending: {
      image: require("./assets/placeholder_cart.gif"),
      imageHeight: 150,
      message: "Hang tight! We're processing\nyour payment securely.",
    },
    success: {
      image: require("./assets/placeholder_success.png"),
      imageHeight: 100,
      message: "Your order is confirmed\nand on its way!",
      subtext: "You'll be redirected shortly.\nPlease don't close this window",
    },
    failed: {
      image: require("./assets/placeholder_failed.png"),
      imageHeight: 90,
      message: "Your payment could not be completed.\nPlease try again.",
    },
  };

  const screen = screens[status];
  if (!screen) return null;

  return (
    <Row justify="center" align="middle" style={{ height: "100vh", backgroundColor: "#fff" }}>
      <Col align="middle">
        <Space direction="vertical" size="large" style={{ marginBottom: 80 }}>
          <img src={screen.image} style={{ height: screen.imageHeight }} alt={status} />
          <h3 className="subtitle" style={{ whiteSpace: "pre-line", fontSize: 18 }}>
            {screen.message}
          </h3>
          {screen.subtext && (
            <div className="description" style={{ whiteSpace: "pre-line" }}>
              {screen.subtext}
            </div>
          )}
          {status === "failed" && (
            <Button size="large" type="primary" danger style={{ width: 140 }} onClick={onRetry}>
              Try Again
            </Button>
          )}
        </Space>
      </Col>
    </Row>
  );
};

const AppLoader = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "100%",
      backgroundColor: "#fff",
    }}
  >
    <Row justify="center" align="middle" style={{ height: "100vh" }} className="fadein">
      <Col align="middle">
        <Space direction="vertical" size="middle" style={{ marginBottom: 40 }}>
          <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Spin
              style={{ position: "absolute" }}
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 80,
                    backgroundColor: "rgba(0,0,0,0.05)",
                    borderRadius: "50%",
                  }}
                />
              }
            />
            <img src="/assets/logo_icon.png" style={{ height: 60, position: "absolute" }} alt="loading" />
          </div>
          <div className="subtext">Please wait while we load your preferences</div>
        </Space>
      </Col>
    </Row>
  </div>
);

export default MainApp;